import React, { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Barcode from "react-barcode";
import { formatNum, unidadeLabel } from "@/lib/format";
import { printArea } from "@/lib/print";

const LabelPrintContext = createContext(null);

function RoloLabel({ rolo, breakAfter }) {
  if (!rolo) return null;
  const u = unidadeLabel(rolo.unidade);
  const isDevolucao = rolo.origem === "Devolução de Sobra";
  const fmtDataBR = (ymd) => {
    const [y, m, d] = String(ymd).split("-");
    return y && m && d ? `${d}/${m}/${y}` : ymd;
  };
  return (
    <div
      style={{
        width: "10cm",
        height: "15cm",
        boxSizing: "border-box",
        padding: 0,
        background: "#fff",
        color: "#000",
        fontFamily: "'IBM Plex Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
        border: "2px solid #000",
        overflow: "hidden",
        breakAfter: breakAfter ? "page" : "auto",
        pageBreakAfter: breakAfter ? "always" : "auto",
      }}
    >
      {isDevolucao && (
        <div
          style={{
            backgroundColor: "#000000",
            color: "#ffffff",
            textAlign: "center",
            fontWeight: 800,
            fontSize: "18pt",
            letterSpacing: "4px",
            padding: "0.25cm 0",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
            colorAdjust: "exact",
          }}
        >
          DEVOLUÇÃO
        </div>
      )}
      <div
        style={{
          flex: 1,
          padding: "0.5cm 0.5cm 1.4cm 0.5cm",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
      <div style={{ textAlign: "center", borderBottom: "2px solid #000", paddingBottom: "0.2cm" }}>
        <div style={{ fontWeight: 700, fontSize: "20pt", letterSpacing: "1px" }}>WEST BRASIL</div>
        <div style={{ fontSize: "10pt", letterSpacing: "2px" }}>ESTOQUE DE TECIDO</div>
      </div>

      <div style={{ textAlign: "center", margin: "0.15cm 0" }}>
        <Barcode value={rolo.codigo} format="CODE128" width={2.2} height={68} fontSize={18} margin={0} />
      </div>

      <div style={{ fontSize: "13pt", lineHeight: 1.5 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 600 }}>Tipo:</span>
          <span>{rolo.tipo}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 600 }}>Cor:</span>
          <span>{rolo.cor}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 600 }}>Fornecedor:</span>
          <span>{rolo.fornecedor_nome}</span>
        </div>
        {isDevolucao && rolo.ref_origem && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 600 }}>REF origem:</span>
            <span style={{ fontWeight: 700 }}>{rolo.ref_origem}</span>
          </div>
        )}
        {rolo.localizacao && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 600 }}>Local:</span>
            <span>{rolo.localizacao}</span>
          </div>
        )}
        {rolo.data_entrada_manual && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 600 }}>Entrada:</span>
            <span>{fmtDataBR(rolo.data_entrada_manual)}</span>
          </div>
        )}
        {rolo.observacao && (
          <div style={{ fontSize: "11pt", lineHeight: 1.25, marginTop: "0.08cm" }}>
            <span style={{ fontWeight: 600 }}>Obs: </span>
            {rolo.observacao}
          </div>
        )}
        <div
          style={{
            borderTop: "2px solid #000",
            marginTop: "0.15cm",
            paddingTop: "0.2cm",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14pt", fontWeight: 600, letterSpacing: "2px" }}>
            {u === "m" ? "MEDIDA" : "PESO"}
          </div>
          <div
            style={{
              fontSize: "34pt",
              fontWeight: 700,
              lineHeight: 1.1,
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            {formatNum(rolo.peso_atual_kg)} {u}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export function LabelPrintProvider({ children }) {
  const [rolos, setRolos] = useState([]);

  useEffect(() => {
    if (!rolos.length) return;
    printArea("west-print-root", "size: 10cm 15cm; margin: 0;");
    const after = () => setRolos([]);
    window.addEventListener("afterprint", after);
    const t = setTimeout(() => setRolos([]), 6000);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", after);
    };
  }, [rolos]);

  const printLabel = (rolo) => setRolos(rolo ? [rolo] : []);
  const printLabels = (arr) => setRolos(Array.isArray(arr) ? arr.filter(Boolean) : []);

  return (
    <LabelPrintContext.Provider value={{ printLabel, printLabels }}>
      {children}
      {createPortal(
        <div className="west-print-root">
          {rolos.map((r, i) => (
            <RoloLabel key={r.id || i} rolo={r} breakAfter={i < rolos.length - 1} />
          ))}
        </div>,
        document.body
      )}
    </LabelPrintContext.Provider>
  );
}

export function useLabelPrint() {
  return useContext(LabelPrintContext);
}

export { RoloLabel };
